import { useNavigate, useSearch } from '@tanstack/react-router';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import { Button, toast } from '@devhop/ui';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import {
    useCompanyPolicyDetailQuery,
    useCreateCompanyPolicyMutation,
} from '../api/queries';
import {
    PolicyCreateTour,
    usePolicyCreateTour,
} from '../tour/PolicyCreateTour';
import { PolicyForm } from '../ui/PolicyForm';

export function PolicyCreatePage() {
    const nav = useNavigate({ from: '/app/hr/company-policy/new' });
    const search = useSearch({ from: '/app/hr/company-policy/new' });
    const duplicateFromId = search?.duplicateFrom;
    const { data: duplicateFromPolicy, isLoading: isLoadingDuplicate } =
        useCompanyPolicyDetailQuery(duplicateFromId ?? undefined);
    const createMutation = useCreateCompanyPolicyMutation();
    const tour = usePolicyCreateTour();

    const handleSubmit = (
        values: import('@/modules/hr/company-policy/domain/types').CreateCompanyPolicyDTO,
    ) => {
        createMutation.mutate(values, {
            onSuccess: (data) => {
                toast.success('ສ້າງນະໂຍບາຍສຳເລັດ');
                nav({
                    to: '/app/hr/company-policy/$id',
                    params: { id: data.id },
                });
            },
            onError: () => {
                toast.error('ບໍ່ສາມາດສ້າງນະໂຍບາຍໄດ້');
            },
        });
    };

    const handleBack = () => {
        nav({
            to: '/app/hr/company-policy',
            search: {
                offset: 0,
                limit: 20,
                policyTypeId: undefined,
                status: undefined,
                search: undefined,
            },
        });
    };

    return (
        <>
            <Header />
            <Main>
                <PolicyCreateTour
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between"
                    data-tourid="policy-create-back"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ເພີ່ມນະໂຍບາຍ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            ສ້າງລະບຽບ ນະໂຍບາຍ ຫຼື ຄູ່ມືການປະຕິບັດໃໝ່
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBack}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບໄປລາຍການ
                    </Button>
                </div>

                <div
                    className="mx-auto max-w-3xl"
                    data-tourid="policy-create-form"
                >
                    {duplicateFromId && isLoadingDuplicate ? (
                        <p className="py-8 text-center text-muted-foreground">
                            ກຳລັງໂຫຼດນະໂຍບາຍເພື່ອສຳເນົາ...
                        </p>
                    ) : (
                        <PolicyForm
                            mode="create"
                            duplicateFrom={duplicateFromPolicy ?? undefined}
                            onSubmit={handleSubmit}
                            onCancel={() =>
                                nav({
                                    to: '/app/hr/company-policy',
                                    search: {
                                        offset: 0,
                                        limit: 20,
                                        policyTypeId: undefined,
                                        status: undefined,
                                        search: undefined,
                                    },
                                })
                            }
                            isSubmitting={createMutation.isPending}
                        />
                    )}
                </div>
            </Main>
        </>
    );
}
